import { Link } from 'react-router-dom'
import { useTeam } from '@/hooks/useTeam'
import { useTeamUsers } from '@/hooks/useTeamUsers'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Users, Trophy, Target, User } from 'lucide-react'

export function Players() {
  const { currentTeam } = useTeam()
  const { data: users, isLoading } = useTeamUsers(currentTeam?.id)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Filter only approved users
  const approvedUsers = users?.filter((u: any) => u.status === 'approved') || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Players</h1>
        <p className="mt-1 text-sm text-gray-500">
          View all players in {currentTeam?.name || 'your team'}
        </p>
      </div>

      {approvedUsers.length === 0 ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No players yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Players will appear here once they join your team.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {approvedUsers.map((player: any) => (
                <Link
                  key={player.id}
                  to={`/players/${player.id}`}
                  className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {player.profile_picture_url ? (
                        <img
                          src={player.profile_picture_url}
                          alt={player.display_name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {player.display_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {player.role === 'admin' ? 'Admin' : 'Member'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="flex items-center space-x-1 text-xs text-gray-600">
                      <Target className="h-3 w-3" />
                      <span>{player.games_played || 0} games</span>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-gray-600">
                      <Trophy className="h-3 w-3 text-yellow-500" />
                      <span>{player.role || 'Member'}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
